import { assertNever } from '@/helpers/utils';

import type { ToolbarItem, ToolbarToolItem } from './types';

import { getConfigEnv } from '../common/helpers/getConfigEnv';
import { ToolId } from './types';

export const TOOL_IDS = [
  // actions
  'cursor',
  'pan',
  'choose',

  // structure
  'wall',
  'obstacle',
  'logical_space',

  // areas
  'intake',
  'storage',
  'parking',
  'charging',
  'delivery',
  'process',
  'manual',
  'insertion',
  'extraction',

  // racking?
  'racking',

  // roads
  'highway',
  'highway_angled',

  // flows
  'transport_flow',
  'consolidation_flow',
  'external_flow',
  'goods_to_persons_flow',

  // templates
  'lassie',
  'lassie_mini',
  'shuttle',
  'hik',

  // connections
  'distcon',

  // reference
  'scale_reference',
  'move_reference',

  // global transform by moving the origin
  'translate_by_move_origin',
] as const;

/**
 * @description A subset of ToolIds that are drawable on the canvas, usually used for things like areas, logical spaces, etc.
 */
export const DRAWABLE_TOOL_IDS = [
  'charging',
  'delivery',
  'intake',
  'manual',
  'insertion',
  'extraction',
  'parking',
  'process',
  'obstacle',
  'storage',
  'logical_space',
  'highway',
  'highway_angled',
  'wall',
  'racking',
] as const satisfies readonly ToolId[];

/**
 * @description A subset of ToolIds that are placeable on the canvas, usually used for things like templates
 */
export const PLACEABLE_TOOL_IDS = [
  'shuttle',
  'lassie',
  'lassie_mini',
] as const satisfies readonly ToolId[];

/**
 * @description A subset of ToolIds that are used for Segments
 */
export const SEGMENT_TOOL_IDS = ['highway_angled', 'wall'] as const satisfies readonly ToolId[];

/**
 * @description Tools that remain available when floorplan is readonly.
 */
export const READONLY_ENABLED_TOOL_IDS: readonly ToolId[] = ['pan', 'cursor'];

export const MENU_IDS = ['process_menu', 'manual_menu', 'areas_menu', 'highway_menu'] as const;

export const actionItems: ToolbarItem[] = [
  {
    type: 'tool',
    id: 'cursor',
    icon: 'cursor',
    tooltip: 'toolbar.labels.actions.select',
    label: 'toolbar.labels.actions.select',
    shortcut: 'V',
  },
  {
    type: 'tool',
    id: 'pan',
    icon: 'pan',
    tooltip: 'toolbar.labels.actions.pan',
    label: 'toolbar.labels.actions.pan',
    shortcut: 'H',
  },
];

const structureItems: ToolbarItem[] = [
  {
    type: 'tool',
    id: 'wall',
    icon: 'wall',
    tooltip: 'toolbar.labels.structure.walls',
    label: 'toolbar.labels.structure.walls',
    shortcut: 'W',
  },
  {
    type: 'tool',
    id: 'obstacle',
    icon: 'obstacle',
    tooltip: 'toolbar.labels.structure.obstacles',
    label: 'toolbar.labels.structure.obstacles',
    shortcut: 'O',
  },
  {
    type: 'tool',
    icon: 'logical_space',
    id: 'logical_space',
    tooltip: 'toolbar.labels.structure.logical_space',
    label: 'toolbar.labels.structure.logical_space',
    shortcut: 'T',
  },
];

const areaItems: ToolbarItem[] = [
  {
    type: 'tool',
    id: 'intake',
    icon: 'intake',
    tooltip: 'toolbar.labels.areas.intake',
    label: 'toolbar.labels.areas.intake',
    shortcut: 'I',
  },
  {
    type: 'tool',
    id: 'storage',
    icon: 'storage',
    tooltip: 'toolbar.labels.areas.storage',
    label: 'toolbar.labels.areas.storage',
    shortcut: 'S',
  },
  {
    type: 'tool',
    id: 'parking',
    icon: 'parking',
    tooltip: 'toolbar.labels.areas.parking',
    label: 'toolbar.labels.areas.parking',
    shortcut: 'P',
  },
  {
    type: 'tool',
    id: 'charging',
    icon: 'charging',
    tooltip: 'toolbar.labels.areas.charging',
    label: 'toolbar.labels.areas.charging',
    shortcut: 'C',
  },
  {
    type: 'tool',
    id: 'delivery',
    icon: 'delivery',
    tooltip: 'toolbar.labels.areas.delivery',
    label: 'toolbar.labels.areas.delivery',
    shortcut: 'D',
  },
  {
    type: 'tool',
    id: 'racking',
    icon: 'racking',
    tooltip: 'toolbar.labels.areas.racking',
    label: 'toolbar.labels.areas.racking',
    shortcut: 'T',
  },
  {
    type: 'tool',
    id: 'process',
    icon: 'process',
    tooltip: 'toolbar.labels.areas.process',
    label: 'toolbar.labels.areas.process',
  },
  {
    type: 'menu',
    icon: null,
    label: 'toolbar.labels.areas.manual.label',
    items: [
      {
        type: 'tool',
        id: 'manual',
        icon: null,
        tooltip: 'toolbar.labels.areas.manual.label',
        label: 'toolbar.labels.areas.manual.label',
        shortcut: 'M',
      },
      {
        type: 'tool',
        id: 'insertion',
        icon: null,
        tooltip: 'toolbar.labels.areas.manual.insertion',
        label: 'toolbar.labels.areas.manual.insertion',
        shortcut: 'Ctrl+I',
      },
      {
        type: 'tool',
        id: 'extraction',
        icon: null,
        tooltip: 'toolbar.labels.areas.manual.extraction',
        label: 'toolbar.labels.areas.manual.extraction',
        shortcut: 'Ctrl+E',
      },
    ],
  },
];

const roadItems: ToolbarItem[] = [
  {
    type: 'tool',
    id: 'highway',
    icon: 'highway',
    tooltip: 'toolbar.labels.highway.highway',
    label: 'toolbar.labels.highway.highway',
    shortcut: 'Shift+H',
  },
  {
    type: 'tool',
    id: 'highway_angled',
    icon: 'highway_angled',
    tooltip: 'toolbar.labels.highway.angled_highway',
    label: 'toolbar.labels.highway.angled_highway',
    shortcut: 'U',
  },
];

const flowItems: ToolbarItem[] = [
  {
    type: 'tool',
    id: 'transport_flow',
    icon: 'transport_flow',
    tooltip: 'toolbar.labels.flows.transport_flow',
    label: 'toolbar.labels.flows.transport_flow',
    shortcut: 'F',
  },
  {
    type: 'tool',
    id: 'consolidation_flow',
    icon: 'consolidation_flow',
    tooltip: 'toolbar.labels.flows.consolidation_flow',
    label: 'toolbar.labels.flows.consolidation_flow',
  },
  {
    type: 'tool',
    id: 'external_flow',
    icon: 'external_flow',
    tooltip: 'toolbar.labels.flows.external_flow',
    label: 'toolbar.labels.flows.external_flow',
  },
];

const templateItems = (): ToolbarItem[] => {
  const env = getConfigEnv();
  let enabledTemplateTools: ToolbarToolItem[] = [
    {
      type: 'tool',
      id: 'lassie',
      icon: 'lassie',
      tooltip: 'toolbar.labels.templates.lassie',
      label: 'toolbar.labels.templates.lassie',
    },
    {
      type: 'tool',
      id: 'lassie_mini',
      icon: 'lassie_mini',
      tooltip: 'toolbar.labels.templates.lassie_mini',
      label: 'toolbar.labels.templates.lassie_mini',
    },
    {
      type: 'tool',
      id: 'shuttle',
      icon: 'shuttle',
      tooltip: 'toolbar.labels.templates.shuttle_rack',
      label: 'toolbar.labels.templates.shuttle_rack',
    },
    {
      type: 'tool',
      id: 'hik',
      icon: 'hik',
      tooltip: 'toolbar.labels.templates.hik',
      label: 'toolbar.labels.templates.hik',
    },
  ];

  switch (env) {
    case 'development':
    case 'test':
      break;
    case 'production':
      enabledTemplateTools = enabledTemplateTools.filter((item) => item.id !== 'lassie_mini');
      break;
    default:
      assertNever(env);
  }

  return enabledTemplateTools;
};

export const toolbarItems: ToolbarItem[] = [
  ...actionItems,
  {
    type: 'menu',
    label: 'toolbar.labels.structure.label',
    items: structureItems,
    icon: null,
  },
  {
    type: 'menu',
    label: 'toolbar.labels.areas.label',
    items: areaItems,
    icon: null,
  },
  {
    type: 'menu',
    label: 'toolbar.labels.highway.label',
    items: roadItems,
    icon: null,
  },
  {
    type: 'menu',
    label: 'toolbar.labels.templates.label',
    items: templateItems(),
    icon: 'lassie',
  },
  {
    type: 'menu',
    label: 'toolbar.labels.flows.label',
    items: flowItems,
    icon: null,
  },
  {
    shortcut: 'Shift+C',
    type: 'tool',
    id: 'distcon',
    icon: 'distcon',
    tooltip: 'toolbar.labels.connections.distcon',
    label: 'toolbar.labels.connections.distcon',
  },
];
